import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Music, Upload } from "lucide-react";
import { api, errMsg, uploadFile, API } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { fmtDate } from "@/lib/format";
import { load } from "@/components/RegionSelect";
import ImageUpload from "@/components/ImageUpload";

function Check({ checked, onChange, label, testid, disabled }) {
  return (
    <label className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-colors ${checked ? "border-emerald-500 bg-emerald-50 font-bold" : "border-slate-200 bg-white"} ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <input data-testid={testid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
      <span className="truncate">{label}</span>
    </label>
  );
}

const toggle = (list, v, on) => (on ? [...list, v] : list.filter((x) => x !== v));

function RegionChecklist({ provinces, setProvinces, regencies, setRegencies, disabled }) {
  const [provList, setProvList] = useState([]);
  const [regMap, setRegMap] = useState({});
  useEffect(() => { load("provinces.json").then(setProvList).catch(() => {}); }, []);
  const pickProv = async (p, on) => {
    setProvinces(toggle(provinces, p.name, on));
    if (on && !regMap[p.name]) {
      const regs = await load(`regencies/${p.id}.json`);
      setRegMap((m) => ({ ...m, [p.name]: regs }));
    }
    if (!on) setRegencies(regencies.filter((r) => !(regMap[p.name] || []).some((x) => x.name === r)));
  };
  return (
    <div className={`space-y-4 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-2">Provinsi</p>
        <div data-testid="push-province-list" className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
          {provList.map((p) => <Check key={p.id} testid={`push-province-${p.id}`} label={p.name} checked={provinces.includes(p.name)} onChange={(on) => pickProv(p, on)} />)}
        </div>
      </div>
      {provinces.map((pn) => (regMap[pn] || []).length > 0 && (
        <div key={pn}>
          <p className="text-xs font-semibold text-slate-600 mb-2">Kabupaten/Kota di {pn} <span className="font-normal text-slate-400">(opsional, kosongkan untuk seluruh provinsi)</span></p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {regMap[pn].map((r) => <Check key={r.id} testid={`push-regency-${r.id}`} label={r.name} checked={regencies.includes(r.name)} onChange={(on) => setRegencies(toggle(regencies, r.name, on))} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SoundUpload({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadFile(f, "branding")); toast.success("Suara diunggah, klik Simpan Pengaturan"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-2">
      <button type="button" data-testid="upload-push-sound" onClick={() => ref.current?.click()} className="h-11 px-4 rounded-xl border-2 border-dashed border-slate-300 text-sm font-bold flex items-center gap-2 hover:border-emerald-400">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{value ? "Ganti Suara" : "Unggah Suara (mp3/wav/ogg)"}
      </button>
      {value && <button type="button" data-testid="preview-push-sound" onClick={() => new Audio(`${API}/public/push-sound?t=${Date.now()}`).play().catch(() => toast.info("Simpan dulu untuk mendengar"))} className="h-11 px-3 rounded-xl bg-slate-100 text-sm font-bold flex items-center gap-1"><Music className="w-4 h-4" />Putar</button>}
      <input ref={ref} type="file" accept="audio/*" hidden onChange={pick} />
    </div>
  );
}

function PushStatus({ l, onCancel }) {
  if (l.status === "scheduled") return <span className="flex items-center gap-2"><span data-testid={`push-status-${l.id}`} className="text-amber-600 font-bold">Terjadwal</span><button data-testid={`push-cancel-${l.id}`} onClick={onCancel} className="text-red-600 underline">Batal</button></span>;
  if (l.status === "cancelled") return <span className="text-slate-400 font-bold">Dibatalkan</span>;
  if (l.status === "sending") return <span className="text-sky-600 font-bold">Mengirim...</span>;
  return l.onesignal_id ? <span className="text-emerald-700 font-bold">Terkirim</span> : <span className="text-red-600" title={l.onesignal_error}>Gagal</span>;
}

export default function AdminPush() {
  const logs = useFetch("/admin/push", { live: (e) => e.type === "admin" });
  const [f, setF] = useState({ title: "", message: "", image_path: null, send_all: false });
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [later, setLater] = useState(false);
  const [when, setWhen] = useState("");
  const send = async (e) => {
    e.preventDefault();
    if (later && !when) return toast.error("Pilih tanggal & jam kirim");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push", { ...f, provinces, regencies, scheduled_at: later ? new Date(when).toISOString() : null });
      if (data.status === "scheduled") toast.success(`Dijadwalkan ${fmtDate(data.scheduled_at)}`);
      else toast[data.onesignal_error ? "warning" : "success"](data.onesignal_error ? `Terkirim in-app ke ${data.in_app_recipients} pengguna, push OneSignal gagal` : `Notifikasi terkirim (${data.in_app_recipients} pengguna in-app)`);
      setF({ title: "", message: "", image_path: null, send_all: false });
      setProvinces([]); setRegencies([]); setLater(false); setWhen("");
      logs.reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const cancel = async (id) => {
    try { await api.delete(`/admin/push/${id}`); toast.success("Jadwal dibatalkan"); logs.reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="max-w-4xl">
      <div className="mb-6"><h1 className="font-display text-3xl font-extrabold tracking-tight">Push Notifikasi</h1><p className="text-slate-500 text-sm">Kirim pesan ke pengguna berdasarkan wilayah atau ke semua</p></div>
      <form onSubmit={send} className="bg-white rounded-2xl border p-5 space-y-5">
        <div className="grid md:grid-cols-[1fr_220px] gap-4">
          <div className="space-y-3">
            <input data-testid="push-title-input" className="field" placeholder="Judul notifikasi" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required maxLength={80} />
            <textarea data-testid="push-message-input" className="field h-28 py-2" placeholder="Isi pesan" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required maxLength={500} />
          </div>
          <div><p className="text-xs font-semibold text-slate-600 mb-1">Gambar (opsional)</p><ImageUpload testid="push-image-upload" kind="push" label="Unggah gambar" className="h-36" value={f.image_path} onChange={(p) => setF((x) => ({ ...x, image_path: p }))} /></div>
        </div>
        <div className="space-y-3">
          <p className="font-display font-bold">Tujuan</p>
          <Check testid="push-send-all" label="Kirim ke semua pengguna" checked={f.send_all} onChange={(v) => setF({ ...f, send_all: v })} />
          <RegionChecklist provinces={provinces} setProvinces={setProvinces} regencies={regencies} setRegencies={setRegencies} disabled={f.send_all} />
        </div>
        <div className="space-y-2">
          <p className="font-display font-bold">Waktu Kirim</p>
          <Check testid="push-schedule-toggle" label="Jadwalkan pengiriman" checked={later} onChange={setLater} />
          {later && <div><input data-testid="push-schedule-input" type="datetime-local" className="field max-w-xs" value={when} onChange={(e) => setWhen(e.target.value)} />
            <p className="text-[11px] text-slate-500 mt-1">Dicek setiap 5 menit, push terkirim paling lambat 5 menit setelah jadwal.</p></div>}
        </div>
        <button data-testid="push-send-btn" disabled={busy} className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{later ? "Jadwalkan Notifikasi" : "Kirim Notifikasi"}
        </button>
      </form>
      <h2 className="font-display font-bold text-lg mt-8 mb-3">Riwayat Pengiriman</h2>
      <div className="bg-white rounded-2xl border overflow-x-auto">
        <table data-testid="push-log-table" className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Tanggal", "Judul", "Tujuan", "In-app", "Push"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody className="divide-y">
            {(logs.data || []).map((l) => (
              <tr key={l.id}><td className="px-4 py-3 text-xs">{fmtDate(l.scheduled_at || l.created_at)}</td><td className="px-4 py-3"><p className="font-bold">{l.title}</p><p className="text-xs text-slate-500 line-clamp-1">{l.message}</p></td>
                <td className="px-4 py-3 text-xs max-w-[200px] truncate">{l.target}</td><td className="px-4 py-3 font-mono">{l.in_app_recipients ?? "-"}</td>
                <td className="px-4 py-3 text-xs"><PushStatus l={l} onCancel={() => cancel(l.id)} /></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
